/**
 * OpenAI Provider Implementation
 * 
 * This module implements the AIProvider interface for OpenAI's API.
 * Supports GPT-4, GPT-4o, GPT-3.5-turbo, and other OpenAI models.
 */

import OpenAI from 'openai';
import { logAiCompletionStats } from '../../../utils/openai/logAiCompletionStats';
import {
  AIProvider,
  AIProviderConfig,
  ChatMessage,
  ChatCompletionOptions,
  ChatCompletionResponse,
} from './types';

export class OpenAIProvider implements AIProvider {
  readonly providerType = 'openai' as const;
  private client: OpenAI;
  private config: AIProviderConfig;

  constructor(apiKey: string, config: AIProviderConfig) {
    this.client = new OpenAI({ apiKey });
    this.config = config;
  }

  async chat(messages: ChatMessage[], options?: ChatCompletionOptions): Promise<ChatCompletionResponse> {
    const response = await this.client.chat.completions.create({
      model: this.config.model,
      messages: messages.map(msg => ({
        role: msg.role,
        content: msg.content,
      })),
      temperature: this.config.temperature,
      max_completion_tokens: this.config.maxTokens,
      ...(options?.jsonMode && {
        response_format: { type: 'json_object' as const },
      }),
    });

    logAiCompletionStats('conversational-search', response);

    const choice = response.choices[0];
    
    return {
      content: choice?.message?.content || '',
      finishReason: choice?.finish_reason || undefined,
      usage: response.usage ? {
        promptTokens: response.usage.prompt_tokens,
        completionTokens: response.usage.completion_tokens,
        totalTokens: response.usage.total_tokens,
      } : undefined,
    };
  }
}

